import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { examsApi, ExamType, DtmDirection, CreateDtmDirectionDto } from '../../api/exams';
import { subjectsApi, Subject } from '../../api/subjects';

interface Props {
  isDark: boolean;
}

const empty: CreateDtmDirectionDto = {
  exam_type_id: '',
  name: '',
  slug: '',
  primary_subject_id: '',
  secondary_subject_id: '',
  primary_question_count: 30,
  secondary_question_count: 30,
  primary_score_per_question: 3.1,
  secondary_score_per_question: 2.1,
  sort_order: 0,
};

export default function DtmDirectionsPage({ isDark }: Props) {
  const [items, setItems] = useState<DtmDirection[]>([]);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DtmDirection | null>(null);
  const [form, setForm] = useState<CreateDtmDirectionDto>(empty);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [dirs, ets, subs] = await Promise.all([
        examsApi.getDirections(),
        examsApi.getExamTypes(),
        subjectsApi.getAll(),
      ]);
      setItems(dirs);
      setExamTypes(ets);
      setSubjects(subs);
      if (!form.exam_type_id) {
        const dtm = ets.find((e) => e.slug === 'dtm');
        if (dtm) setForm((p) => ({ ...p, exam_type_id: dtm.id }));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleOpenCreate = () => {
    setEditing(null);
    const dtm = examTypes.find((e) => e.slug === 'dtm');
    setForm({ ...empty, exam_type_id: dtm?.id || examTypes[0]?.id || '' });
    setOpen(true);
  };

  const handleEdit = (d: DtmDirection) => {
    setEditing(d);
    setForm({
      exam_type_id: d.exam_type_id,
      name: d.name,
      slug: d.slug,
      primary_subject_id: d.primary_subject_id,
      secondary_subject_id: d.secondary_subject_id,
      primary_question_count: d.primary_question_count,
      secondary_question_count: d.secondary_question_count,
      primary_score_per_question: Number(d.primary_score_per_question),
      secondary_score_per_question: Number(d.secondary_score_per_question),
      sort_order: d.sort_order,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await examsApi.updateDirection(editing.id, {
          name: form.name,
          primary_question_count: form.primary_question_count,
          secondary_question_count: form.secondary_question_count,
          primary_score_per_question: form.primary_score_per_question,
          secondary_score_per_question: form.secondary_score_per_question,
          sort_order: form.sort_order,
        });
        toast.success('Direction updated');
      } else {
        await examsApi.createDirection(form);
        toast.success('Direction created');
      }
      setOpen(false);
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save direction');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this direction?')) return;
    try {
      await examsApi.deleteDirection(id);
      toast.success('Direction deleted');
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  const subjectName = (id: string) => subjects.find((s) => s.id === id)?.name || '—';

  const inputCls = cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200');
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');
  const headCls = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#0F172A]',
            )}
          >
            DTM Directions
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Subject pairs (e.g. Matematika - Fizika) and their scoring config
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
          <Plus className="w-4 h-4 mr-2" /> New Direction
        </Button>
      </div>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden',
          isDark ? 'bg-[#1E293B] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50',
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}>
                <TableHead className={headCls}>Name</TableHead>
                <TableHead className={headCls}>Primary</TableHead>
                <TableHead className={headCls}>Secondary</TableHead>
                <TableHead className={headCls}>Scoring</TableHead>
                <TableHead className={headCls}>Order</TableHead>
                <TableHead className={headCls}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    No directions
                  </TableCell>
                </TableRow>
              ) : (
                items.map((d) => (
                  <TableRow
                    key={d.id}
                    className={cn(
                      isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50',
                    )}
                  >
                    <TableCell className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>
                      {d.name}
                      <div className="text-xs text-slate-500">/{d.slug}</div>
                    </TableCell>
                    <TableCell className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                      {d.primary_subject?.name || subjectName(d.primary_subject_id)} ({d.primary_question_count})
                    </TableCell>
                    <TableCell className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                      {d.secondary_subject?.name || subjectName(d.secondary_subject_id)} ({d.secondary_question_count})
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {Number(d.primary_score_per_question)} / {Number(d.secondary_score_per_question)}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{d.sort_order}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(d)} className="h-8 px-2">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(d.id)}
                          className="h-8 px-2 text-slate-500 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className={cn(
            'max-w-2xl max-h-[90vh] overflow-y-auto',
            isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
          )}
        >
          <DialogHeader>
            <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
              {editing ? 'Edit direction' : 'Create direction'}
            </DialogTitle>
            <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
              Pair two subjects and configure question counts and scoring.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Exam type *</Label>
                <Select
                  value={form.exam_type_id}
                  onValueChange={(v) => setForm((p) => ({ ...p, exam_type_id: v }))}
                  disabled={!!editing}
                >
                  <SelectTrigger className={inputCls}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {examTypes.map((et) => (
                      <SelectItem key={et.id} value={et.id}>
                        {et.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Sort order</Label>
                <Input
                  type="number"
                  value={form.sort_order ?? 0}
                  onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) || 0 }))}
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Matematika - Fizika"
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Slug *</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                  disabled={!!editing}
                  placeholder="matematika-fizika"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Primary subject *</Label>
                <Select
                  value={form.primary_subject_id}
                  onValueChange={(v) => setForm((p) => ({ ...p, primary_subject_id: v }))}
                  disabled={!!editing}
                >
                  <SelectTrigger className={inputCls}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Secondary subject *</Label>
                <Select
                  value={form.secondary_subject_id}
                  onValueChange={(v) => setForm((p) => ({ ...p, secondary_subject_id: v }))}
                  disabled={!!editing}
                >
                  <SelectTrigger className={inputCls}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Primary Q count</Label>
                <Input
                  type="number"
                  value={form.primary_question_count ?? 30}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, primary_question_count: Number(e.target.value) || 0 }))
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Secondary Q count</Label>
                <Input
                  type="number"
                  value={form.secondary_question_count ?? 30}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, secondary_question_count: Number(e.target.value) || 0 }))
                  }
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Primary score / Q</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.primary_score_per_question ?? 3.1}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, primary_score_per_question: Number(e.target.value) || 0 }))
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Secondary score / Q</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.secondary_score_per_question ?? 2.1}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, secondary_score_per_question: Number(e.target.value) || 0 }))
                  }
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">
              {editing ? 'Save changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
